<template>
  <div v-click-outside="hideDropdown" class="dropdown">
    <div :id="`case_${caseId}-disabled`">
      <button
        :id="`case_${caseId}_detail_dropdown`"
        class="dropdown-more"
        @click="showDropdown"
      >
        <slot />
      </button>
    </div>
    <ul
      class="dropdown-list"
      :class="{'active': dropdown}"
      :style="{ transform: $route.params.slug
        ? 'translate(-50%, 100%)'
        : 'translate(-90%, 100%)'
      }"
    >
      <li v-if="hasPermission(['export_case'])" class="dropdown-list-item">
        <button class="list-item-btn import">
          <input
            :id="`import_messages_input_${caseId}`"
            ref="fileInput"
            type="file"
            accept=".zip, .ufdr"
            class="import-messages-input"
            @change="onFileChange"
          >
          <label :for="`import_messages_input_${caseId}`" class="import-messages-label">
            <span class="list-item-icon">
              <GreyImportIcon24 />
            </span>
            <span>Import messages</span>
          </label>
        </button>
      </li>
      <li
        v-if="!$route.params.slug && hasPermission(['export_case'])"
        class="dropdown-list-item dropdown-list-item-export"
      >
        <button
          id="case_detail_dropdown_export"
          class="list-item-btn"
          @click="goToExportCasePage()"
        >
          <span class="list-item-icon">
            <GreyDownloadIcon24 />
          </span>
          Download all data
        </button>
      </li>
      <li class="dropdown-list-item dropdown-list-item-email">
        <button
          v-if="hasPermission(['view_case'])"
          :id="`case_detail_dropdown_email`"
          class="list-item-btn"
          @click="$router.push({path: `/cases/${caseId}/emails`}).catch(()=>{})"
        >
          <span class="list-item-icon">
            <GreyEmailIcon24 />
          </span>
          E-mail integration
        </button>
      </li>
      <li
        v-if="!$route.params.slug && hasPermission(['configuration_case'])"
        class="dropdown-list-item dropdown-list-item-settings"
      >
        <button
          :id="`case_detail_dropdown_settings`"
          class="list-item-btn"
          @click="$router.push({path: `/cases/${caseId}/settings`}).catch(()=>{})"
        >
          <span class="list-item-icon">
            <GreySettingsIcon24 />
          </span>
          Settings
        </button>
      </li>
      <li
        v-if="hasPermission(['remove_case'])"
        class="dropdown-list-item dropdown-list-item-delete"
        @click.stop="deleteCase"
      >
        <button
          :id="`case_detail_dropdown_delete_${caseId}`"
          class="list-item-btn"
        >
          <span class="list-item-icon">
            <RedDeleteIcon24 />
          </span>
          Delete
        </button>
      </li>
    </ul>
    <CaseActionsPopup
      ref="actionsPopup"
      :action-type="actionType"
      :case-id="caseId"
      :archive-payment="archivePayment"
      @confirm="caseActionConfirm"
    />
    <ConfirmPopup
      ref="confirmCaseDeletion"
      description="Are you sure you want to delete case?"
      title="Deleting a case"
      @confirm="confirmCaseDeletion"
    />
    <AlertErrorMsg
      ref="alert"
      :title="errorMessage"
    />
    <CommonDialog
      ref="progressDialog"
      :show-footer="false"
      :closable="false"
    >
      <h2 class="dialog-message-title">
        Importing messages
      </h2>
      <p class="dialog-create-description" style="display:block; font-size: 14px;">
        Please wait a bit. The messages are
        being imported.
      </p>
      <div class="loader-p" />
    </CommonDialog>
    <SuccessPopup
      ref="successPopup"
      :text="`Messages were imported to this case`"
    />
  </div>
</template>

<script>
import hasPermission from '../../../utils/permission';
import CaseActionsPopup from '../../popups/CaseActionsPopup';
import ConfirmPopup from '@/components/popups/ConfirmPopup';
import AlertErrorMsg from '@/components/profile/AlertErrorMsg';
import SuccessPopup from '@/components/popups/SuccessPopup.vue';
import CommonDialog from '@/components/popups/CommonDialog.vue';
import capitalizeFirstLetter from '@/mixins/capitalizeFirstLetter';
import streamReader from '@/mixins/streamReader';
import errorHandler from '@/mixins/errorHandler';
import GreyImportIcon24 from '@/components/icons/GreyImportIcon24.vue';
import GreyDownloadIcon24 from '@/components/icons/GreyDownloadIcon24.vue';
import GreyEmailIcon24 from '@/components/icons/GreyEmailIcon24.vue';
import RedDeleteIcon24 from '@/components/icons/RedDeleteIcon24.vue';
import GreySettingsIcon24 from '@/components/icons/GreySettingsIcon24.vue';

export default {
  name: 'CaseDropdown',
  components: {
    GreySettingsIcon24,
    RedDeleteIcon24,
    GreyEmailIcon24,
    GreyDownloadIcon24,
    GreyImportIcon24,
    CommonDialog,
    SuccessPopup,
    AlertErrorMsg,
    ConfirmPopup,
    CaseActionsPopup
  },
  emits: ['setCreds'],
  mixins: [capitalizeFirstLetter, streamReader, errorHandler],
  props: {
    customClass: {
      type: String,
      required: false,
      default: '',
    },
    caseIdProp: {
      type: String,
      default: '',
    },
    tooltipPlacement: {
      type: String,
      default: '',
    },
    caseName: {
      type: String,
      required: true,
    }
  },
  data() {
    return {
      dropdown: false,
      actionType: '',
      archivePayment: '30',
      errorMessage: '',
    };
  },
  computed: {
    caseId() {
      return this.$route.params.slug || this.caseIdProp;
    }
  },
  methods: {
    hasPermission,
    pushToManageCustodiansPage() {
      localStorage.setItem('title', this.caseName);
      this.$router.push({path: `/cases/${this.caseId}/management`}).catch(() => {
      });
    },
    showDropdown() {
      this.dropdown = !this.dropdown;
    },
    hideDropdown() {
      this.dropdown = false;
    },
    caseActionConfirm() {
      this.$refs.confirmCaseDeletion.openPopups();
      this.$refs.actionsPopup.closePopup();
    },
    deleteCase() {
      this.actionType = 'delete';
      this.$refs.actionsPopup.openPopup();
      this.dropdown = false;
    },
    confirmCaseDeletion() {
      this.$load(() => {
        this.$api.cases.deleteCase(this.caseId).then(() => {
          this.$refs.confirmCaseDeletion.closePopup();
          this.$emit('caseDeleted');
          this.$router.push({name: 'Cases'}).catch(() => {
          });
        }).catch(error => {
          this.handleResponseError(error);
          this.$refs.confirmCaseDeletion.closePopup();
        });
      });
    },
    onFileChange(e) {
      const selectedFile = e.target.files[0];
      const fileExtension = selectedFile.name.split('.').pop();
      const formData = new FormData();
      formData.append('file', selectedFile);
      this.$refs.fileInput.value = null;
      this.importMessages(formData, fileExtension);
    },
    importMessages(data, fileExtension) {
      if (fileExtension === 'ufdr') {
        this.$load(async () => {
          this.$refs.progressDialog.openPopups();
          await this.$api.cases.importUfdr(this.caseId, data).then(async (response) => {
            // eslint-disable-next-line no-undef
            const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
            await this.processStreamData(reader, 25);
            this.handleStreamResult();
          }, (error) => {
            this.handleResponseError(error);
          });
        });
      } else {
        this.$load(async () => {
          this.$refs.progressDialog.openPopups();
          await this.$api.cases.importMessages(this.caseId, data).then(async (response) => {
            // eslint-disable-next-line no-undef
            const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
            await this.processStreamData(reader, 25);
            this.handleStreamResult();
          }, (error) => {
            this.handleResponseError(error);
          });
        });
      }
    },
    goToExportCasePage() {
      this.$emit('setCreds');
      this.$router.push({path: `/cases/${this.caseId}/export`}).catch(() => {
      });
    },
  },
};
</script>

<style scoped>
.dropdown {
  width: 100%;
  max-width: 104px;
  margin: 0;
  position: relative;
}

.dropdown-more {
  height: 35px;
  max-width: 104px;
  width: 100%;
  font-weight: 500;
  font-size: 12px;
  line-height: 120%;
  vertical-align: middle;
  color: var(--secondary-text-color);
  border: 1px solid var(--secondary-text-color);
  background-color: var(--white-color);
  border-radius: 40px;
  outline: none;
  transition: .5s all;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.dropdown-more >>> svg path {
  transition: .5s all;
}

.dropdown-more:hover,
.dropdown-more:hover >>> svg path {
  border-color: var(--accent-color);
  color: var(--accent-color);
  fill: var(--accent-color);
}

.dropdown-list {
  position: absolute;
  bottom: 0;
  left: -1px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(100%);
  overflow: hidden;
  transition: .5s all;
  margin: 0;
  box-shadow: 0 4px 39px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  min-width: 180px;
  padding-top: 10px;
  padding-bottom: 10px;
  width: 100%;
  background-color: var(--white-color);
}

.dropdown-list.active {
  visibility: visible;
  opacity: 1;
  z-index: 1;
}

.dropdown-list-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: .5s all;
  cursor: pointer;
}

.dropdown-list-item:hover {
  background-color: var(--blue-bg-color);
}

.dropdown-list-item-delete .list-item-btn {
  color: #F1105F;
}

.dropdown-list-item-delete .list-item-icon svg path {
  fill: #F1105F;
}

.list-item-btn {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 5px;
  background-color: transparent;
  border: none;
  outline: none;
  padding: 8px;
  font-size: 14px;
  font-weight: 500;
  color: var(--secondary-text-color);
}

.list-item-btn.import {
  padding: 0;
}

.list-item-icon {
  width: 24px;
  height: 24px;
}

.list-item-icon svg {
  max-width: 100%;
  max-height: 100%;
}

button:disabled {
  pointer-events: none;
}

.import-messages-label {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 5px;
  padding: 8px;
  cursor: pointer;
  margin: 0;
}

.import-messages-input {
  display: none;
}

.dialog-message-title {
  font-style: normal;
  font-weight: bold;
  font-size: 30px;
  line-height: 36px;
  color: #292B2F;
  text-align: center;
  margin: 25px 0 0;
  display: block;
  width: 100%;
}

.dialog-message-description {
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 26px;
  text-align: center;
  color: #696F79;
  display: block;
  width: 100%;
  margin-top: 20px;
}

.dialog-create-description {
  font-size: 14px;
  font-weight: 500;
  line-height: 17px;
  text-align: center;
  color: var(--light-gray);
  margin: 10px 0 0;
}

.dialog-info-wrapper {
  display: flex;
  flex-direction: column;
  width: 406px;
  margin: 25px 20px 50px;
  border-radius: 6px;
  background-color: var(--hover-accent-color);
}

.dialog-info-text {
  display: block;
  font-size: 18px;
  font-weight: 500;
  line-height: 27px;
  text-align: center;
  color: var(--accent-color);
  margin: 20px 15px;
}

.progress-bar {
  width: 345px;
  background-color: #EBEFF6;
  border-radius: 5px;
  overflow: hidden;
  margin: 0 30px 30px;
}

.progress {
  height: 5px;
  background-color: var(--accent-color);
  width: 0;
  transition: width 0.3s;
}

.loader-p {
  width: 50px;
  height: 50px;
  position: relative;
  margin: 20px 40px 20px 0;
}

.disabled {
  pointer-events: none;
  opacity: 0.4;
}
</style>
